import React, { useState, useEffect, useRef } from 'react';
import './ProductShowcase.css';
import { useLanguage } from '../context/LanguageContext';

const ProductShowcase = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const sectionRef = useRef(null);
  const { t } = useLanguage();
  
  const products = t.products.items;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-slide effect
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % products.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isPaused, products.length]);

  const nextProduct = () => {
    setActiveIndex((prev) => (prev + 1) % products.length);
  };

  const prevProduct = () => {
    setActiveIndex((prev) => (prev - 1 + products.length) % products.length);
  };

  return (
    <section className="product-showcase" id="collection" ref={sectionRef}>
      <div className="showcase-header">
        <span className="section-label">{t.products.sectionLabel}</span>
        <h2 className="section-title">
          <span>{t.products.sectionTitle1}</span>
          <span className="accent">{t.products.sectionTitle2}</span>
        </h2>
      </div>

      <div className={`showcase-container ${isInView ? 'visible' : ''}`}>
        <div className="showcase-nav">
          <div className="nav-counter">
            <span className="current">{String(activeIndex + 1).padStart(2, '0')}</span>
            <span className="separator">/</span>
            <span className="total">{String(products.length).padStart(2, '0')}</span>
          </div>
        </div>

        <button className="slider-arrow slider-arrow-left" onClick={() => { prevProduct(); setIsPaused(true); setTimeout(() => setIsPaused(false), 5000); }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <button className="slider-arrow slider-arrow-right" onClick={() => { nextProduct(); setIsPaused(true); setTimeout(() => setIsPaused(false), 5000); }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div 
          className="products-wrapper"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {products.map((product, index) => (
            <div 
              key={index}
              className={`product-card ${index === activeIndex ? 'active' : ''}`}
              style={{
                transform: `translateX(${(index - activeIndex) * 110}%)`,
                opacity: index === activeIndex ? 1 : 0,
                visibility: Math.abs(index - activeIndex) <= 1 ? 'visible' : 'hidden'
              }}
            >
              <div className="product-visual">
                <div className="product-glow"></div>
                <div 
                  className="product-bottle"
                  style={{
                    transform: `scale(${index === 0 ? 0.6 : index === 1 ? 0.75 : index === 2 ? 0.9 : 1.05})`,
                    transformOrigin: 'center bottom'
                  }}
                >
                  <img 
                    src="/VULKANO VODKA MAIN BOTTLE.png" 
                    alt={product.name}
                    className="product-bottle-img"
                  />
                </div>
              </div>

              <div className="product-info">
                <span className="product-subtitle">{product.subtitle}</span>
                <h3 className="product-name">{product.name}</h3>
                <p className="product-description">{product.description}</p>
                
                <div className="product-features">
                  {product.features.map((feature, i) => (
                    <span key={i} className="feature">{feature}</span>
                  ))}
                </div>

                <div className="product-footer">
                  <div className="product-price">
                    <span className="price">{product.price}</span>
                    <span className="limited">{product.limited}</span>
                  </div>
                  <button className="add-btn">
                    <span>{t.products.reserve}</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="product-indicators">
          {products.map((_, index) => (
            <button
              key={index}
              className={`indicator ${index === activeIndex ? 'active' : ''} ${!isPaused && index === activeIndex ? 'animating' : ''}`}
              onClick={() => { setActiveIndex(index); setIsPaused(true); setTimeout(() => setIsPaused(false), 5000); }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
